import {isString} from 'lodash';
import React, {FC, useEffect, useRef, useState} from 'react';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {AsyncHandlerOf, Handler} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {hasProperty} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {useBuildCancelToken, usePrevious} from '../../../../../srcCommon/helpers/hookHelpers';
import {buildCancelTokenSource, CancelError} from '../../../../helpers/asyncCancelHelpers';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {CrmHubspotFieldModel} from '../../../../models/api/crmHubspotFieldModel';
import {CrmHubspotMetadataModel} from '../../../../models/api/crmHubspotMetadataModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncModel,
  CrmSyncSourcesEnum,
} from '../../../../models/api/crmSyncModel';
import {CustomFieldModel} from '../../../../models/api/customFieldModel';
import {SyncJobModel} from '../../../../models/api/jobModel';
import {ApiSendOptions} from '../../../../services/http/http';
import {HttpResponseError} from '../../../../services/http/httpError';
import {
  fetchHubspotFieldMetadataAsyncAction,
  fetchHubspotFieldsAsyncAction,
} from '../../../../store/data/crmSync/crmSyncActions';
import {CrmPreviewParentPanelId} from '../../../../store/panels/contacts/contactsManager/crmPreview/crmPreviewActions';
import {computeCrmSyncMapping} from '../common/crmSyncHelpers';
import {useMappedFields} from '../common/hooks/useMappedFields';
import {useUpdateCrmSync} from '../common/hooks/useUpdateCrmSync';
import {HubspotCrmSyncDialog} from './hubspotCrmSyncDialog';
import {HubspotOptionalDefaultFields} from './hubspotHelpers';

/*
 * Props.
 */

interface HubspotCrmSyncDialogWrapperProps {
  panelId: CrmPreviewParentPanelId;
  hasCrmSyncFeature: boolean;
  isAdmin: boolean;
  crmSync?: CrmSyncModel;
  username?: string;
  isConnected: boolean;
  shouldStartOnErrorsPage?: boolean;
  activeSyncJob?: SyncJobModel;
  customFields: CustomFieldModel[];
  disableCrmSyncAsync: AsyncHandlerOf<number>;
  onRequestClose: Handler;
  fetchOrCreateCrmSyncResourceAsync: (
    source: CrmSyncSourcesEnum,
    resourceType: CrmSyncIntegrationResourceTypesEnum,
    options: ApiSendOptions,
  ) => Promise<void>;
}

/*
 * Component.
 */

export const HubspotCrmSyncDialogWrapper: FC<HubspotCrmSyncDialogWrapperProps> = (props) => {
  const {
    panelId,
    crmSync,
    hasCrmSyncFeature,
    isAdmin,
    username,
    isConnected,
    activeSyncJob,
    customFields,
    shouldStartOnErrorsPage,
    disableCrmSyncAsync,
    onRequestClose,
    fetchOrCreateCrmSyncResourceAsync,
  } = props;
  const dispatch = useAppDispatch();

  const [cancelToken] = useState(buildCancelTokenSource());
  // Cancel token for fetch fields request.
  const {buildCancelToken} = useBuildCancelToken();

  const [hasExpiredCredentials, setHasExpiredCredentials] = useState(false);
  const [hubspotMetadata, setHubspotMetadata] = useState<CrmHubspotMetadataModel>();
  const [hasTriedFetching, setHasTriedFetching] = useState(false);
  const [hubspotFields, setHubspotFields] = useState<ReadonlyArray<CrmHubspotFieldModel>>();
  const contentWrapperRef = useRef<HTMLDivElement>(null);
  const {
    mappedFields,
    hasInvalidMappedField,
    onResetMappedFields,
    onRemoveMappedField,
    onAddMappedField,
    onUpdateMappedField,
  } = useMappedFields(contentWrapperRef, customFields, crmSync, hubspotFields);
  const {isUpdatingCrmSync, onUpdateCrmSyncAsync} = useUpdateCrmSync(crmSync, mappedFields, onRequestClose);

  const previousCrmSync = usePrevious(crmSync);
  const isConnectedAndValid = isConnected && !hasExpiredCredentials;
  const previousIsConnectedAndValid = usePrevious(isConnectedAndValid);
  const previousIsConnected = usePrevious(isConnected);

  // Only show a loading state if we dont have a crmSync object and we have not tried looking for one.
  // If we tried looking for one and still do not have one, that means we do not have the integration connected.
  const isLoading = !crmSync && !hasTriedFetching;

  const fetchOrCreateAsync = async () => {
    try {
      await fetchOrCreateCrmSyncResourceAsync(
        CrmSyncSourcesEnum.HUBSPOT,
        CrmSyncIntegrationResourceTypesEnum.ACCOUNT,
        {
          cancelToken: cancelToken.token,
        },
      );
      setHasTriedFetching(true);
    } catch (error) {
      // Do nothing with errors from here.
    }
  };
  // Fetches or creates a new crm sync object.
  useInitialEffect(() => {
    fetchOrCreateAsync();
    return () => cancelToken.cancel();
  });

  useEffect(() => {
    // If the credentials have expired, we should just show the default fields.
    if (!isConnectedAndValid) {
      onResetMappedFields(HubspotOptionalDefaultFields);
      return;
    }

    const wasSyncJustLoaded = previousCrmSync === undefined && crmSync;
    const wasIntegrationJustConnected = previousIsConnectedAndValid === false && isConnectedAndValid;
    if (wasSyncJustLoaded || wasIntegrationJustConnected) {
      onResetMappedFields(computeCrmSyncMapping(crmSync));
      setHasExpiredCredentials(false);
    }
  }, [
    crmSync,
    previousCrmSync,
    hasExpiredCredentials,
    previousIsConnectedAndValid,
    isConnectedAndValid,
    onResetMappedFields,
  ]);

  useEffect(() => {
    // If we are not connected, just set the value as an empty array
    if (previousIsConnected === false && isConnected === false) {
      setHubspotFields([]);
    }
    // If we just connected, we should reset all the values.
    if (previousIsConnected === false && isConnected) {
      setHubspotFields(undefined);
      setHasExpiredCredentials(false);
    }
  }, [isConnected, previousIsConnected]);

  // Fetch the hubspot metadata.
  useEffect(() => {
    const fetchMappingAsync = async () => {
      try {
        const fieldMapping = await dispatch(
          fetchHubspotFieldMetadataAsyncAction(CrmSyncIntegrationResourceTypesEnum.ACCOUNT, {
            cancelToken: cancelToken.token,
          }),
        );
        setHubspotMetadata(fieldMapping);
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        if (
          error instanceof HttpResponseError &&
          hasProperty(error.body, 'message') &&
          isString(error.body.message) &&
          error.body.message.includes('Invalid Hubspot Credentials')
        ) {
          setHasExpiredCredentials(true);
        }
      }
    };

    // If we do not have a sync object or we already have a mapping, or we need to log in again, return early.
    if (!crmSync?.id || hubspotMetadata || !isConnected || hasExpiredCredentials) {
      return;
    }
    fetchMappingAsync();
  }, [crmSync?.id, hubspotMetadata, dispatch, cancelToken.token, hasExpiredCredentials, isConnected]);

  // Fetch the hubspot fields and mapping.
  useEffect(() => {
    // If we do not have a sync object or we already have the fields, return early.
    if (
      !crmSync?.id ||
      !isConnected ||
      (hubspotFields && hubspotFields.length > 0) ||
      hasExpiredCredentials
    ) {
      return;
    }

    const fetchFieldsAsync = async () => {
      try {
        const fields = await dispatch(
          fetchHubspotFieldsAsyncAction(CrmSyncIntegrationResourceTypesEnum.ACCOUNT, {
            cancelToken: buildCancelToken(),
          }),
        );
        setHubspotFields((crmFields) => {
          if (fields && fields.length > 0) {
            return fields;
          }
          return crmFields ?? [];
        });
      } catch (error) {
        if (error instanceof CancelError) {
          return;
        }
        if (
          error instanceof HttpResponseError &&
          hasProperty(error.body, 'message') &&
          isString(error.body.message) &&
          error.body.message?.includes('Invalid Hubspot Credentials')
        ) {
          setHasExpiredCredentials(true);
        }
      }
    };

    fetchFieldsAsync();
  }, [crmSync?.id, isConnected, hubspotFields, hasExpiredCredentials, dispatch, buildCancelToken]);

  return (
    <HubspotCrmSyncDialog
      panelId={panelId}
      crmSync={crmSync}
      activeSyncJob={activeSyncJob}
      isAdmin={isAdmin}
      isLoading={isLoading}
      hasInvalidMappedField={hasInvalidMappedField}
      username={username}
      customFields={customFields}
      hubspotMetadata={hubspotMetadata}
      hubspotFields={hubspotFields}
      contentWrapperRef={contentWrapperRef}
      mappedFields={mappedFields}
      isUpdatingCrmSync={isUpdatingCrmSync}
      shouldStartOnErrorsPage={shouldStartOnErrorsPage}
      disableCrmSyncAsync={disableCrmSyncAsync}
      onUpdateCrmSyncAsync={onUpdateCrmSyncAsync}
      onRemoveMappedField={onRemoveMappedField}
      onAddMappedField={onAddMappedField}
      onUpdateMappedField={onUpdateMappedField}
      onRequestClose={onRequestClose}
      hasCrmSyncFeature={hasCrmSyncFeature}
      hasExpiredCredentials={hasExpiredCredentials}
      isConnected={isConnected}
    />
  );
};
